import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { TrendingUp, TrendingDown, Minus, Activity } from "lucide-react";
import { format, subMonths } from "date-fns";

const biomarkerInfo = {
  glucose: {
    name: "Glucose",
    unit: "mg/dL",
    optimal: { min: 70, max: 100 },
    color: "#3b82f6",
  },
  cholesterol_total: {
    name: "Total Cholesterol",
    unit: "mg/dL",
    optimal: { min: 150, max: 200 },
    color: "#8b5cf6",
  },
  cholesterol_hdl: {
    name: "HDL Cholesterol",
    unit: "mg/dL",
    optimal: { min: 40, max: 100 },
    color: "#10b981",
  },
  cholesterol_ldl: {
    name: "LDL Cholesterol",
    unit: "mg/dL",
    optimal: { min: 0, max: 100 },
    color: "#f59e0b",
  },
  triglycerides: {
    name: "Triglycerides",
    unit: "mg/dL",
    optimal: { min: 0, max: 150 },
    color: "#ef4444",
  },
  hemoglobin: {
    name: "Hemoglobin",
    unit: "g/dL",
    optimal: { min: 12, max: 16 },
    color: "#ec4899",
  },
};

export default function BiomarkerTrends({ tests, timeRange }) {
  const processedData = React.useMemo(() => {
    if (!tests || tests.length === 0) return {};

    const now = new Date();
    let startDate;

    switch (timeRange) {
      case "3months":
        startDate = subMonths(now, 3);
        break;
      case "6months":
        startDate = subMonths(now, 6);
        break;
      case "1year":
        startDate = subMonths(now, 12);
        break;
      default:
        startDate = subMonths(now, 6);
    }

    const filteredTests = tests
      .filter((test) => new Date(test.test_date) >= startDate && test.results)
      .sort((a, b) => new Date(a.test_date) - new Date(b.test_date));

    const biomarkerData = {};

    Object.keys(biomarkerInfo).forEach((biomarker) => {
      biomarkerData[biomarker] = filteredTests
        .filter((test) => test.results[biomarker] !== undefined)
        .map((test) => ({
          date: format(new Date(test.test_date), "MMM dd"),
          fullDate: test.test_date,
          value: test.results[biomarker],
          testType: test.test_type,
        }));
    });

    return biomarkerData;
  }, [tests, timeRange]);

  const getTrendDirection = (data) => {
    if (data.length < 2) return "stable";
    const first = data[0].value;
    const last = data[data.length - 1].value;
    const change = ((last - first) / first) * 100;

    if (Math.abs(change) < 5) return "stable";
    return change > 0 ? "up" : "down";
  };

  const getTrendIcon = (direction) => {
    switch (direction) {
      case "up":
        return TrendingUp;
      case "down":
        return TrendingDown;
      default:
        return Minus;
    }
  };

  const getTrendColor = (biomarker, direction, data) => {
    if (data.length === 0) return "text-gray-500";

    const latest = data[data.length - 1].value;
    const optimal = biomarkerInfo[biomarker].optimal;
    const isInOptimal = latest >= optimal.min && latest <= optimal.max;

    if (isInOptimal) return "text-green-600";
    if (direction === "stable") return "text-blue-600";

    // For glucose, cholesterol, etc., going up is usually bad
    const increaseIsBad = [
      "glucose",
      "cholesterol_total",
      "cholesterol_ldl",
      "triglycerides",
    ];
    if (increaseIsBad.includes(biomarker)) {
      return direction === "up" ? "text-red-600" : "text-green-600";
    }

    // For HDL, hemoglobin, going up is usually good
    return direction === "up" ? "text-green-600" : "text-red-600";
  };

  const CustomTooltip = ({ active, payload, label, biomarker }) => {
    if (active && payload && payload.length) {
      const info = biomarkerInfo[biomarker];
      return (
        <div className="bg-white/95 backdrop-blur-sm p-3 rounded-lg shadow-xl border border-slate-200">
          <p className="font-medium text-slate-900">{label}</p>
          <p style={{ color: info.color }}>
            {info.name}: {payload[0].value} {info.unit}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Optimal: {info.optimal.min}-{info.optimal.max} {info.unit}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-600" />
          Biomarker Trends Analysis
        </CardTitle>
        <p className="text-sm text-slate-600">
          Track your key health biomarkers over time with trend analysis
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Object.entries(processedData).map(([biomarker, data]) => {
            if (data.length === 0) return null;

            const info = biomarkerInfo[biomarker];
            const trend = getTrendDirection(data);
            const TrendIcon = getTrendIcon(trend);
            const trendColor = getTrendColor(biomarker, trend, data);
            const latest = data[data.length - 1];

            return (
              <div key={biomarker} className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      {info.name}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-600">
                        Latest: {latest.value} {info.unit}
                      </span>
                      <TrendIcon className={`w-4 h-4 ${trendColor}`} />
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={`${
                      latest.value >= info.optimal.min &&
                      latest.value <= info.optimal.max
                        ? "border-green-200 text-green-700 bg-green-50"
                        : "border-orange-200 text-orange-700 bg-orange-50"
                    }`}
                  >
                    {latest.value >= info.optimal.min &&
                    latest.value <= info.optimal.max
                      ? "Optimal"
                      : "Attention"}
                  </Badge>
                </div>

                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={data}
                      margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" stroke="#64748b" fontSize={10} />
                      <YAxis
                        stroke="#64748b"
                        fontSize={10}
                        domain={["dataMin - 10", "dataMax + 10"]}
                      />

                      <ReferenceLine
                        y={info.optimal.max}
                        stroke="#ef4444"
                        strokeDasharray="3 3"
                        strokeWidth={1}
                      />
                      <ReferenceLine
                        y={info.optimal.min}
                        stroke="#ef4444"
                        strokeDasharray="3 3"
                        strokeWidth={1}
                      />

                      <Tooltip
                        content={(props) => (
                          <CustomTooltip {...props} biomarker={biomarker} />
                        )}
                      />

                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke={info.color}
                        strokeWidth={2}
                        dot={{ fill: info.color, strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, stroke: info.color, strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
